import { Injectable, inject } from '@angular/core';
// ─────────────────────────────────────────────────────────────────────────────
// Duck Db
//
// Type-only import: erased at compile time, so it does NOT pull the ~16MB
// runtime wrapper into the eager bundle graph. The implementation is loaded on
// demand in init() via dynamic import (EHQ-91).
import type * as duckdb from '@duckdb/duckdb-wasm';
// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
import { Chunk } from '../../interfaces/step/step.interface';
import { GeneralHelpers } from '../../helpers/general.helper';
import { RenderService } from '../render/render.service';

import {
  FILE_EXTENSIONS,
  RELOAD_WASM,
  REMOVE_DUCKDB_FILE,
  SYNC_DUCKDB_FILES,
} from '../../constants/general.constants';
import { MessageService } from '../message/message.service';
import { Subscription } from 'rxjs';
import { LocalforageService } from '../localforage/localforage.service';
import { tableFromIPC, tableToIPC } from 'apache-arrow';
import { FileModule } from '../../interfaces/file.interface';
import { FileService } from '../file/file.service';
import { Papa } from 'ngx-papaparse';
import { from } from 'arquero';
import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import { FileUnifiedService } from '../file-unified/file-unified.service';
import { ConvertService } from '../convert/convert.service';

@Injectable({
  providedIn: 'root',
})
export class DuckDbService {
  private renderService = inject(RenderService);
  private messageService = inject(MessageService);
  private readonly localforageService = inject(LocalforageService);
  private fileService = inject(FileService);
  private papa = inject(Papa);
  private fileUnifiedService = inject(FileUnifiedService);
  private convertService = inject(ConvertService);

  private database!: duckdb.AsyncDuckDB;
  private connection!: duckdb.AsyncDuckDBConnection;
  private worker!: Worker;
  private logger!: duckdb.ConsoleLogger;

  /**
   * The dynamically-imported duckdb-wasm module. Populated by init(); anything
   * needing duckdb values at runtime must go through here rather than a
   * top-level import, which would put the wrapper back on the critical path
   * (EHQ-91).
   */
  private duckdbModule?: typeof duckdb;

  // File list
  public fileList: string[] = [];

  // ─────────────────────────────────────────────────────────────────────
  // Message subscription
  private messageSubscription!: Subscription;
  private pendingSyncRequests = false;

  constructor() {
    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (message && message.text === REMOVE_DUCKDB_FILE) {
          this.removeFileFromDatabase(message?.data);
        }
        if (message && message.text === RELOAD_WASM) {
          setTimeout(() => {
            if (!this.database) {
              console.warn('DuckDB not initialized yet, skipping WASM reload');
              return;
            }
            // this.dropAllTables();
            this.dropFiles();
            this.flushFiles();
            this.reset();
          }, 0);
        }
        if (message && message.text === SYNC_DUCKDB_FILES) {
          setTimeout(() => {
            if (!this.database) {
              this.pendingSyncRequests = true;
              console.warn('DuckDB not initialized, queuing sync request');
              return;
            }
            this.processGlobalModules();
          }, 0);
        }
      });
  }

  public async init() {
    try {
      // Loaded on demand so the duckdb wrapper stays out of the initial bundle
      // and its wasm is fetched after first paint (EHQ-91).
      const duckdbModule = this.duckdbModule ?? (await import('@duckdb/duckdb-wasm'));
      this.duckdbModule = duckdbModule;

      const JSDELIVR_BUNDLES = duckdbModule.getJsDelivrBundles();
      const bundle = await duckdbModule.selectBundle(JSDELIVR_BUNDLES);

      const worker_url = URL.createObjectURL(
        new Blob([`importScripts("${bundle.mainWorker!}");`], {
          type: 'text/javascript',
        })
      );

      this.worker = new Worker(worker_url);
      this.logger = new duckdbModule.ConsoleLogger();
      this.database = new duckdbModule.AsyncDuckDB(this.logger, this.worker);
      await this.database.instantiate(bundle.mainModule, bundle.pthreadWorker);
      URL.revokeObjectURL(worker_url);
      this.processGlobalModules();

      // Handle pending sync requests
      if (this.pendingSyncRequests) {
        console.log('🔄 Processing pending sync requests');
        this.processGlobalModules();
        this.pendingSyncRequests = false;
      }
    } catch (error) {
      console.error('Failed to initialize DuckDB:', error);
      throw error;
    }
  }

  private async ensureInitialized() {
    if (!this.database) {
      try {
        await this.init();
      } catch (error) {
        console.error('Failed to initialize DuckDB on demand:', error);
        throw new Error('DuckDB failed to initialize. Please refresh the page.');
      }
    }
  }

  public async doQuery(chunkData: Chunk, context: ChunkContext) {
    const { content } = chunkData;

    if (typeof content !== 'string' || !!content === false) {
      return;
    }
    await this.openConnection();
    const data = await this.connection.query(content);

    const dataString = data.toString();
    const isJSON = GeneralHelpers.isJSON(dataString);
    if (isJSON) {
      this.renderService.renderTable([dataString], context);
    } else {
      const aquero = from(data).objects();
      this.renderService.renderTable([aquero], context);
    }

    await this.closeConnection();
  }

  // ----------------------------------------------
  // Control connection
  // ----------------------------------------------
  private async openConnection() {
    if (!this.database) {
      throw new Error('DuckDB database is not initialized. Call init() first.');
    }
    this.connection = await this.database.connect();
  }

  private async closeConnection() {
    await this.connection.close();
  }

  // ----------------------------------------------
  // Control connection
  // ----------------------------------------------

  // ----------------------------------------------
  // Remove file from DB
  // ----------------------------------------------

  private async removeFileFromDatabase(name: string) {
    const filePosition = this.fileList.findIndex(
      (fileName) => fileName === name
    );
    if (filePosition > -1) {
      this.fileList.splice(filePosition, 1);
    }
    if (!this.database) {
      console.warn('DuckDB database is not initialized. Call init() first.');
      return;
    }
    await this.database.dropFile(name);
  }

  // ----------------------------------------------
  // Control panel methods (PUBLIC)
  // ----------------------------------------------

  public async dropFiles() {
    if (!this.database) {
      console.warn('DuckDB database is not initialized. Call init() first.');
      return;
    }
    await this.database.dropFiles();
  }

  public async flushFiles() {
    if (!this.database) {
      console.warn('DuckDB database is not initialized. Call init() first.');
      return;
    }
    await this.database.flushFiles();
  }

  public async reset() {
    if (!this.database) {
      console.warn('DuckDB database is not initialized. Call init() first.');
      return;
    }
    await this.database.reset();
  }

  public async uploadFile(data: File) {
    if (!this.database || !this.duckdbModule) {
      console.warn('DuckDB database is not initialized. Call init() first.');
      return;
    }
    await this.database.registerFileHandle(
      data.name,
      data,
      this.duckdbModule.DuckDBDataProtocol.BROWSER_FILEREADER,
      true
    );
  }

  // ----------------------------------------------
  // Control panel methods (PUBLIC)
  // ----------------------------------------------

  private async insertArrowFromIPCStream(
    buffer: Uint8Array,
    options: any = {}
  ) {
    try {
      await this.openConnection();
      await this.connection.insertArrowFromIPCStream(buffer, {
        schema: 'main',
        ...options,
      });
    } catch (error) {
      console.log(error);
    } finally {
      await this.closeConnection();
    }
  }

  private async processGlobalModules() {
    const list: FileModule[] = await this.fileService.getFileModuleList([
      FILE_EXTENSIONS.csv,
      FILE_EXTENSIONS.json,
      FILE_EXTENSIONS.parquet,
      FILE_EXTENSIONS.arrow,
    ]);
    const hasModules = Array.isArray(list) && list.length > 0;
    if (hasModules) {
      for (let index = 0; index < list.length; index++) {
        const element = list[index];
        this.addModule(element);
      }
    }
  }

  private async addModule(element: FileModule) {
    const { name, extension } = element;
    if (this.fileList.includes(name)) {
      return;
    }
    const isFile = await this.checkFileByKey(name);
    if (isFile) {
      return;
    }
    if (!this.database) {
      console.warn('DuckDB database is not initialized. Skipping module:', name);
      return;
    }
    try {
      switch (extension) {
        case FILE_EXTENSIONS.csv: {
          const csvData: unknown = await this.localforageService.getItem(name);
          if (csvData) {
            this.database.registerFileText(name, csvData as string);
          }
          break;
        }
        case FILE_EXTENSIONS.json: {
          const jsonData: any = await this.localforageService.getItem(name);
          if (jsonData) {
            const parsed = this.papa.unparse(jsonData);
            const csvName = name.substr(0, name.lastIndexOf('.')) + '.csv';
            this.database.registerFileText(csvName, parsed);
          }
          break;
        }
        case FILE_EXTENSIONS.parquet: {
          const parquetData: any = await this.localforageService.getItem(name);
          if (parquetData) {
            this.database.registerFileBuffer(name, new Uint8Array(parquetData));
          }
          break;
        }
        case FILE_EXTENSIONS.arrow: {
          const arrowData: any = await this.localforageService.getItem(name);
          if (arrowData) {
            const buffer = this.processArrowData(arrowData);
            this.insertArrowFromIPCStream(buffer, {
              name: name,
              create: true,
            });
          }
          break;
        }
        default:
          break;
      }
      this.fileList.push(name);
    } catch (e) {
      console.error(e);
    }
  }

  private processArrowData(arrowData: ArrayBuffer): Uint8Array {
    const table = tableFromIPC(arrowData);
    return tableToIPC(table);
  }

  private async checkFileByKey(key: string) {
    if (!this.database) {
      console.warn('DuckDB database is not initialized. Cannot check file:', key);
      return false;
    }
    const files: duckdb.WebFile[] = await this.database.globFiles(key);
    if (files.length > 0) {
      return true;
    }
    return false;
  }

  private async dropAllTables() {
    await this.openConnection();
    const tables = await this.connection.getTableNames('*');
    for (const element of tables) {
      const tableName = element;
      await this.connection.query(`DROP TABLE ${tableName}`);
    }
    await this.closeConnection();
  }

  private async checkTableExists(tableName: string) {
    await this.openConnection();
    const tables = await this.connection.query('SHOW TABLES;');
    const tablesString = tables.toString();
    console.log('Existing tables', tablesString);
    await this.closeConnection();
    return tablesString.includes(tableName);
  }
}
